import { useState, useRef } from "react";
import { X, Sparkles, Loader2, Upload, Image as ImageIcon, Pencil } from "lucide-react";

const models = [
  { id: "deepseek-v3", name: "DeepSeek V3", tag: "文本对话", available: true },
  { id: "deepseek-v2", name: "DeepSeek V2", tag: "文本对话", available: true },
  { id: "gpt-4o", name: "GPT-4o", tag: "图片理解", available: true },
  { id: "gpt-4o-mini", name: "GPT-4o Mini", tag: "图片理解", available: true },
  { id: "qwen-vl-max", name: "通义千问 VL", tag: "图片理解", available: false },
  { id: "glm-4v", name: "智谱 GLM-4V", tag: "图片理解", available: false },
  { id: "hunyuan", name: "腾讯混元", tag: "文本对话", available: false },
];

interface CreatePersonaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (persona: { icon: string; name: string; model: string; description: string }) => void;
}

export function CreatePersonaModal({ isOpen, onClose, onCreate }: CreatePersonaModalProps) {
  const [avatar, setAvatar] = useState<string | null>(null);
  const [aiPrompt, setAiPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [name, setName] = useState("");
  const [model, setModel] = useState(models[0].id);
  const [description, setDescription] = useState("");
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 处理图片上传
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        alert("请上传图片文件");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert("图片大小不能超过 5MB");
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (event) => {
        const result = event.target?.result as string;
        // base64 图片是同步加载的，不需要骨架屏
        setAvatar(result);
      };
      reader.readAsDataURL(file);
    }
  };

  // 触发文件选择
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  // AI 生成头像
  const handleGenerateAvatar = async () => {
    if (!aiPrompt.trim() || isGenerating) return;
    
    setIsGenerating(true);
    
    try {
      const response = await fetch("/api/generate-image", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt: aiPrompt.trim(),
          size: "512x512"
        }),
      });
      
      const data = await response.json();
      
      if (data.imageUrl) {
        setIsImageLoading(true);
        setAvatar(data.imageUrl);
        if (data.fallback) {
          console.log("Using fallback avatar:", data.message);
        }
      } else if (data.predictionId) {
        let status = "processing";
        let attempts = 0;
        
        while (status === "processing" && attempts < 30) {
          await new Promise(r => setTimeout(r, 1000));
          const pollRes = await fetch(`/api/prediction/${data.predictionId}`);
          const pollData = await pollRes.json();
          
          if (pollData.status === "succeeded" && pollData.output?.[0]) {
            setIsImageLoading(true);
            setAvatar(pollData.output[0]);
            break;
          }
          status = pollData.status;
          attempts++;
        }
      }
    } catch (error) {
      console.error("Failed to generate avatar:", error);
      const seed = encodeURIComponent(aiPrompt.trim());
      const fallbackUrl = `https://api.dicebear.com/7.x/bottts-neutral/svg?seed=${seed}`;
      setAvatar(fallbackUrl);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSubmit = () => {
    if (name.trim() && avatar) {
      onCreate({
        icon: avatar,
        name: name.trim(),
        model: model,
        description: description.trim() || `使用 ${models.find(m => m.id === model)?.name} 的人格`,
      });
      // 重置表单
      setAvatar(null);
      setAiPrompt("");
      setName("");
      setModel(models[0].id);
      setDescription("");
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* 遮罩 */}
      <div 
        className="absolute inset-0 bg-black/30"
        onClick={onClose}
      />
      
      {/* 弹窗 */}
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6 max-h-[90vh] overflow-y-auto">
        {/* 关闭按钮 */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 transition"
        >
          <X className="w-5 h-5" />
        </button>

        {/* 标题 */}
        <h2 className="text-xl font-semibold text-gray-900 mb-6">创建自定义人格</h2>

        {/* 头像区域 */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            人格头像
          </label>
          
          {/* 隐藏的文件输入 */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            accept="image/*"
            className="hidden"
          />
          
          {/* 头像预览 + 编辑按钮 */}
          <div className="flex justify-center mb-4">
            <div className="relative">
              {avatar ? (
                <div className="relative group">
                  {/* 图片加载中显示骨架屏 */}
                  {isImageLoading && (
                    <div className="absolute inset-0 w-24 h-24 rounded-full bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 animate-pulse z-10" />
                  )}
                  <img
                    src={avatar}
                    alt="头像预览"
                    className={`w-24 h-24 rounded-full object-cover border-2 border-gray-200 transition-opacity duration-300 ${isImageLoading ? 'opacity-0' : 'opacity-100'}`}
                    onLoad={() => setIsImageLoading(false)}
                    onError={() => setIsImageLoading(false)}
                  />
                  {/* 编辑按钮 - 悬停或始终显示 */}
                  <button
                    type="button"
                    onClick={handleUploadClick}
                    className="absolute bottom-0 right-0 w-8 h-8 bg-white border-2 border-gray-200 rounded-full flex items-center justify-center text-gray-500 hover:text-blue-500 hover:border-blue-400 transition shadow-sm"
                    title="更换头像"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  {/* 删除按钮 */}
                  <button
                    type="button"
                    onClick={() => {
                      setAvatar(null);
                      setIsImageLoading(false);
                    }}
                    className="absolute top-0 right-0 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600 z-10"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <div 
                  onClick={handleUploadClick}
                  className="w-24 h-24 rounded-full bg-gray-50 border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition"
                >
                  <ImageIcon className="w-10 h-10 text-gray-400" />
                </div>
              )}
            </div>
          </div>

          {/* AI 生成输入区 */}
          <div className="flex gap-2">
            <input
              type="text"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder="一句话描述头像风格，如：可爱的机器人、戴眼镜的猫..."
              className="flex-1 px-3 py-2.5 rounded-lg border border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none transition text-sm text-gray-900 placeholder-gray-400"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleGenerateAvatar();
                }
              }}
            />
            <button
              type="button"
              onClick={handleGenerateAvatar}
              disabled={!aiPrompt.trim() || isGenerating}
              className="px-4 py-2.5 rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shrink-0 text-sm font-medium min-w-[100px] justify-center"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  生成中
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  AI 生成
                </>
              )}
            </button>
          </div>
          
          <p className="text-xs text-gray-400 text-center mt-2">
            💡 AI 生成使用 Ideogram V3 Turbo 模型
          </p>
          
          {isGenerating && (
            <p className="text-xs text-blue-500 text-center mt-1">
              正在生成头像，请稍候...
            </p>
          )}
        </div>

        {/* 人格名称 */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            人格名称 <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="例如：产品专家"
            className="w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none transition text-gray-900 placeholder-gray-400"
          />
        </div>

        {/* 选择大模型 */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            选择大模型 <span className="text-red-400">*</span>
          </label>
          <div className="space-y-2">
            {models.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => setModel(m.id)}
                disabled={!m.available}
                className={`w-full px-4 py-3 rounded-lg border-2 transition text-left flex items-center justify-between ${
                  model === m.id 
                    ? "border-blue-500 bg-blue-50" 
                    : m.available 
                      ? "border-gray-200 hover:border-gray-300 bg-white" 
                      : "border-gray-100 bg-gray-50 cursor-not-allowed opacity-60"
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className={`font-medium ${m.available ? "text-gray-900" : "text-gray-400"}`}>
                    {m.name}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    m.tag === "图片理解" 
                      ? "bg-purple-100 text-purple-600" 
                      : "bg-green-100 text-green-600"
                  }`}>
                    {m.tag}
                  </span>
                </div>
                {!m.available && (
                  <span className="text-xs text-gray-400">待配置</span>
                )}
                {m.available && model === m.id && (
                  <span className="text-blue-500">✓</span>
                )}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-2">
            💡 "图片理解"模型支持上传图片并识别内容
          </p>
        </div>

        {/* 人格描述 */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            人格描述
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="描述这个人格的特点、风格、专长..."
            rows={3}
            className="w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none transition text-gray-900 placeholder-gray-400 resize-none"
          />
        </div>

        {/* 底部按钮 */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition font-medium"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={!name.trim() || !avatar}
            className="flex-1 py-2.5 rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            创建人格
          </button>
        </div>
      </div>
    </div>
  );
}
